import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useMagicLinkVerify } from '@/hooks/use-auth-api'
import { useAuth } from '@/providers/auth-provider'

type VerificationStatus = 'verifying' | 'success' | 'error'

export function MagicLinkPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isAuthenticated } = useAuth()
  const verifyMutation = useMagicLinkVerify()

  const [status, setStatus] = useState<VerificationStatus>('verifying')
  const [errorMessage, setErrorMessage] = useState('')

  const token = searchParams.get('token')

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  // Verify the magic link token
  useEffect(() => {
    if (!token) {
      setStatus('error')
      setErrorMessage('No magic link token provided. Please request a new link.')
      return
    }

    const verifyToken = async () => {
      try {
        await verifyMutation.mutateAsync({ token })
        setStatus('success')
        toast.success('Successfully signed in!')
        // Navigate after a brief delay to show success state
        setTimeout(() => {
          navigate('/dashboard', { replace: true })
        }, 1500)
      } catch (error: any) {
        setStatus('error')
        setErrorMessage(
          error.message || 'Invalid or expired magic link. Please request a new one.'
        )
      }
    }

    verifyToken()
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              status === 'verifying' ? 'bg-muted' :
              status === 'success' ? 'bg-green-100 dark:bg-green-900/20' :
              'bg-red-100 dark:bg-red-900/20'
            }`}>
              {status === 'verifying' && (
                <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
              )}
              {status === 'success' && (
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              )}
              {status === 'error' && (
                <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              )}
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            {status === 'verifying' && 'Signing you in...'}
            {status === 'success' && 'Welcome back!'}
            {status === 'error' && 'Sign in failed'}
          </CardTitle>
          <CardDescription className="text-center">
            {status === 'verifying' && 'Please wait while we verify your magic link'}
            {status === 'success' && 'You have been successfully signed in'}
            {status === 'error' && errorMessage}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status === 'verifying' && (
            <div className="flex justify-center py-4">
              <p className="text-sm text-muted-foreground">
                This should only take a moment...
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                Redirecting you to your dashboard...
              </p>
              <Button
                className="w-full"
                onClick={() => navigate('/dashboard', { replace: true })}
              >
                Go to Dashboard
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <Link to="/login">
                <Button className="w-full">
                  <Mail className="mr-2 h-4 w-4" />
                  Request a new link
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  Back to login
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
